import { useCallback, useSyncExternalStore } from "react";
import type { DockviewApi, IDockviewPanelProps } from "dockview-react";
import {
  LINK_GROUP_COLORS,
  LINK_GROUPS,
  normalizeSymbol,
  parseAccountFilter,
  parseAccountsViewMode,
  useLinkGroups,
  type AccountFilter,
  type AccountsViewMode,
  type LinkTarget,
} from "@helm/core";
import { setAccountSelection } from "../viewmodel/account";
import { SymbolDropdown } from "../components/SymbolDropdown";
import { Icon } from "../components/Icon";
import {
  DEFAULT_PAD_MODE,
  WIDGET_TITLES,
  type PadMode,
  type WidgetKind,
  type WidgetParams,
} from "./templates";
// Trading surfaces load with the shell (entry chunk): order pads, DOM, positions / orders tables.
import { OrderPad } from "../widgets/OrderPad";
import { DomLadder } from "../widgets/DomLadder";
import { TimeSales } from "../widgets/TimeSales";
import { Watchlist } from "../widgets/Watchlist";
import { lazyComponent } from "../components/lazy";
import {
  AccountsTable,
  FillsTable,
  NotificationsTable,
  OrdersTable,
  PositionsPanel,
} from "../widgets/BottomTables";
import type { TablesParam } from "../tables/useTableColumns";
import { useChartModuleChrome, useHeaderless } from "./panelChrome";
import { InDockContext } from "./dockContext";
import { DetailsView } from "../sidebar/DetailsView";

// Heavy or rarely used modules load on first use (audit S-12); none of them can place or close
// an order that the entry chunk cannot.
const ChartPanel = lazyComponent(() => import("../chart/ChartPanel").then((m) => m.ChartPanel), "chart");
const Screener = lazyComponent(() => import("../widgets/Screener").then((m) => m.Screener), "screener");
const AlertsPanel = lazyComponent(() => import("../widgets/Alerts").then((m) => m.AlertsPanel), "alerts");
const CopyTraderPanel = lazyComponent(
  () => import("../copy/CopyTraderPanel").then((m) => m.CopyTraderPanel),
  "copy trader",
);
const DailyJournal = lazyComponent(
  () => import("../widgets/DailyJournal").then((m) => m.DailyJournal),
  "journal",
);

/**
 * Panel bodies. Each carries the shared chrome (link dot, symbol); trading surfaces are rendered
 * disabled until the order core exists (SPEC-ADDENDA A2 guardrail).
 */

/** Widgets that take single-key trading hotkeys when focused (SPEC §9.10). */
const TRADING_SCOPE: ReadonlySet<WidgetKind> = new Set(["chart", "dom", "execution", "scalp-pad", "ticket"]);
/** Widgets whose bar shows no symbol (account-level). */
const ACCOUNT_LEVEL: ReadonlySet<WidgetKind> = new Set([
  "positions",
  "orders",
  "fills",
  "accounts",
  "notifications",
  "risk",
  "journal",
]);

function LinkDot({
  link,
  onChange,
}: {
  link: LinkTarget;
  onChange: (l: LinkTarget) => void;
}): React.JSX.Element {
  return (
    <select
      className="link-dot"
      value={link}
      aria-label="Link group"
      title="Link group (SPEC §12.9)"
      style={{ color: link === "unlinked" ? "var(--text-3)" : LINK_GROUP_COLORS[link] }}
      onChange={(e) => {
        onChange(e.target.value as LinkTarget);
      }}
    >
      <option value="unlinked">○ unlinked</option>
      {LINK_GROUPS.map((g) => (
        <option key={g} value={g}>
          ● {g}
        </option>
      ))}
    </select>
  );
}

/** Distinct link colours the dock's modules use ("unlinked" not counted). */
function linkGroupsInUse(api: DockviewApi): number {
  const used = new Set<string>();
  for (const p of api.panels) {
    const l = (p.params as WidgetParams | undefined)?.link;
    if (l && l !== "unlinked") used.add(l);
  }
  return used.size;
}

/**
 * Link-colour dots show only while 2+ link groups are in use (owner 2026-10-04): with one colour
 * (or none) they say nothing. The link stays settable from the module "⋯" menu.
 */
function useShowLinkDots(api: DockviewApi): boolean {
  const subscribe = useCallback(
    (cb: () => void) => {
      const a = api.onDidLayoutChange(cb);
      const b = api.onDidAddPanel(cb);
      const c = api.onDidRemovePanel(cb);
      return () => {
        a.dispose();
        b.dispose();
        c.dispose();
      };
    },
    [api],
  );
  return useSyncExternalStore(subscribe, () => linkGroupsInUse(api) >= 2);
}

/**
 * "+ DOM": another ladder for another ticker (Tradovate: several DOMs side by side). It opens
 * unlinked on this DOM's symbol, beside it; pick its ticker from its own dropdown, or give it a
 * link colour to follow a chart.
 */
function AddDomButton({
  api,
  symbol,
}: {
  api: IDockviewPanelProps<WidgetParams>["containerApi"];
  symbol: string | null;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="btn btn-sm btn-ghost dom-add"
      title="Add another DOM (unlinked) for another ticker"
      aria-label="Add another DOM"
      onClick={() => {
        const id = `dom-${String(Date.now())}`;
        const params: WidgetParams = { kind: "dom", link: "unlinked", ...(symbol ? { symbol } : {}) };
        const ref = api.activePanel;
        api.addPanel({
          id,
          component: "widget",
          title: WIDGET_TITLES.dom,
          params,
          ...(ref ? { position: { referencePanel: ref.id, direction: "right" as const } } : {}),
        });
      }}
    >
      <Icon name="plus" size={11} /> DOM
    </button>
  );
}

export function PanelFrame(props: IDockviewPanelProps<WidgetParams>): React.JSX.Element {
  const { kind, link } = props.params;
  const mode: PadMode = props.params.mode ?? DEFAULT_PAD_MODE[kind] ?? "dom";
  const groups = useLinkGroups((s) => s.groups);
  const setGroupSymbol = useLinkGroups((s) => s.setSymbol);
  const own = typeof props.params.symbol === "string" ? normalizeSymbol(props.params.symbol) : null;
  const symbol = link === "unlinked" ? own : groups[link].symbol;
  const setLink = (l: LinkTarget): void => {
    // Unlinking keeps the symbol the module shows (Tradovate: grey = linking off, same ticker).
    const keep = l === "unlinked" && symbol !== null ? { symbol } : {};
    props.api.updateParameters({ ...props.params, ...keep, link: l });
  };
  /** Symbol dropdown: an unlinked module keeps its own; a linked one retargets its colour. */
  const pickSymbol = (s: string): void => {
    if (link === "unlinked") props.api.updateParameters({ ...props.params, symbol: s });
    else setGroupSymbol(link, s);
  };
  const accountLevel = ACCOUNT_LEVEL.has(kind);
  const showDots = useShowLinkDots(props.containerApi);
  if (kind === "chart") {
    // Split-chart module (1–8 panes); a layout-"1" chart looks and saves as before.
    return <ChartFrame {...props} setLink={setLink} showDot={showDots} />;
  }
  return (
    <div
      className="widget"
      data-kind={kind}
      data-trading-scope={TRADING_SCOPE.has(kind) ? kind : undefined}
      aria-label={WIDGET_TITLES[kind]}
      role="region"
    >
      {!accountLevel && (
        <div className="widget-bar">
          {showDots && <LinkDot link={link} onChange={setLink} />}
          <span className="widget-symbol sr-only">{symbol ?? "—"}</span>
          <SymbolDropdown
            symbol={symbol}
            onPick={pickSymbol}
            label={`${WIDGET_TITLES[kind]} symbol`}
            title={
              link === "unlinked"
                ? "Change this module's symbol (unlinked: only this module changes)"
                : `Change the ${link} link group's symbol: every ${link} module follows (charts too)`
            }
          />
          {kind === "dom" && <AddDomButton api={props.containerApi} symbol={symbol} />}
        </div>
      )}
      <div className={accountLevel ? "widget-body widget-body-full" : "widget-body"}>
        <InDockContext.Provider value={true}>
          <Body
            kind={kind}
            link={link}
            symbol={symbol}
            mode={mode}
            domJoinCollapsed={props.params.domJoinCollapsed ?? false}
            domControls={props.params.domControls}
            onDomControlsChange={(controls) => {
              props.api.updateParameters({ ...props.params, domControls: controls });
            }}
            onDomJoinCollapsedChange={(collapsed) => {
              props.api.updateParameters({ ...props.params, domJoinCollapsed: collapsed });
            }}
            onModeChange={(m) => {
              props.api.updateParameters({ ...props.params, mode: m });
            }}
            account={parseAccountFilter(props.params.account)}
            onAccountChange={(f) => {
              props.api.updateParameters({ ...props.params, account: f });
            }}
            accountsView={parseAccountsViewMode(props.params.accountsView)}
            onAccountsViewChange={(v) => {
              props.api.updateParameters({ ...props.params, accountsView: v });
            }}
            tables={props.params.tables}
            onTablesChange={(t) => {
              props.api.updateParameters({ ...props.params, tables: t });
            }}
            onOpenJournal={() => {
              const api = props.containerApi;
              const existing = api.panels.find(
                (p) => (p.params as WidgetParams | undefined)?.kind === "journal",
              );
              if (existing) existing.api.setActive();
              else
                api.addPanel({
                  id: `journal-${String(Date.now())}`,
                  component: "widget",
                  title: WIDGET_TITLES.journal,
                  params: { kind: "journal", link: "unlinked" },
                });
            }}
          />
        </InDockContext.Provider>
      </div>
    </div>
  );
}

/**
 * A chart module. In Tradovate style, while its group is headerless (panelChrome.tsx), its bar also
 * carries the module chrome: drag grip + the "⋯" menu (maximize / float / pop out) + close.
 */
function ChartFrame(
  props: IDockviewPanelProps<WidgetParams> & { setLink: (l: LinkTarget) => void; showDot: boolean },
): React.JSX.Element {
  const headerless = useHeaderless(props.api, props.containerApi);
  const chrome = useChartModuleChrome({ panelApi: props.api, containerApi: props.containerApi });
  return (
    <ChartPanel
      params={props.params}
      panelId={props.api.id}
      linkDot={props.showDot ? <LinkDot link={props.params.link} onChange={props.setLink} /> : null}
      update={(p) => {
        props.api.updateParameters(p);
      }}
      moduleChrome={headerless ? chrome : undefined}
    />
  );
}

function Body({
  kind,
  link,
  symbol,
  mode,
  onModeChange,
  domJoinCollapsed,
  onDomJoinCollapsedChange,
  domControls,
  onDomControlsChange,
  account,
  onAccountChange,
  accountsView,
  onAccountsViewChange,
  tables,
  onTablesChange,
  onOpenJournal,
}: {
  kind: WidgetKind;
  link: LinkTarget;
  symbol: string | null;
  mode: PadMode;
  onModeChange: (m: PadMode) => void;
  domJoinCollapsed: boolean;
  onDomJoinCollapsedChange: (collapsed: boolean) => void;
  domControls: WidgetParams["domControls"];
  onDomControlsChange: (controls: NonNullable<WidgetParams["domControls"]>) => void;
  account: AccountFilter;
  onAccountChange: (f: AccountFilter) => void;
  accountsView: AccountsViewMode;
  onAccountsViewChange: (m: AccountsViewMode) => void;
  tables: unknown;
  onTablesChange: (t: TablesParam) => void;
  onOpenJournal: () => void;
}): React.JSX.Element {
  switch (kind) {
    case "dom":
      return <DomLadder symbol={symbol} />;
    case "execution":
    case "scalp-pad":
    case "ticket":
      return (
        <OrderPad
          symbol={symbol}
          mode={mode}
          onModeChange={onModeChange}
          domJoinCollapsed={domJoinCollapsed}
          onDomJoinCollapsedChange={onDomJoinCollapsedChange}
          domControls={domControls}
          onDomControlsChange={onDomControlsChange}
        />
      );
    case "details":
      return <DetailsView symbol={symbol} />;
    case "tape":
      return <TimeSales symbol={symbol} />;
    case "watchlist":
      return <Watchlist link={link} />;
    case "screener":
      return <Screener link={link} />;
    case "alerts":
      return <AlertsPanel symbol={symbol} />;
    case "positions":
      return (
        <PositionsPanel
          onOpenJournal={onOpenJournal}
          account={account}
          onAccountChange={onAccountChange}
          tables={tables}
          onTablesChange={onTablesChange}
        />
      );
    case "orders":
      return (
        <OrdersTable
          account={account}
          onAccountChange={onAccountChange}
          tables={tables}
          onTablesChange={onTablesChange}
        />
      );
    case "fills":
      return (
        <FillsTable
          account={account}
          onAccountChange={onAccountChange}
          tables={tables}
          onTablesChange={onTablesChange}
        />
      );
    case "accounts":
      return (
        <AccountsTable
          mode={accountsView}
          onModeChange={onAccountsViewChange}
          onShowAccount={setAccountSelection}
          tables={tables}
          onTablesChange={onTablesChange}
        />
      );
    case "notifications":
      return <NotificationsTable />;
    case "journal":
      return <DailyJournal />;
    case "risk":
      return (
        <div className="empty-state">
          <div className="empty-title">Rule tracker</div>
          <div className="muted">Assign a firm profile after connecting a Rithmic login.</div>
        </div>
      );
    case "copy":
      return <CopyTraderPanel />;
    default:
      return (
        <div className="empty-state">
          <div className="muted">{WIDGET_TITLES[kind]} arrives in a later phase.</div>
        </div>
      );
  }
}
